import { useCallback, useEffect, useRef, useState } from 'react'

/** Debounced autosave with a "Saved / Saving…" status the UI can show next
 *  to the title field. `save` receives no args — it should read from the
 *  ref/state closure it was created with. Call `markDirty()` after any
 *  imperative mutation of the editor's state object. */
export function useAutosave(save, delay = 1500) {
  const [status, setStatus] = useState('idle') // idle | dirty | saving | saved | error
  const timer = useRef(null)
  const savingRef = useRef(false)

  const flush = useCallback(async () => {
    if (savingRef.current) return
    savingRef.current = true
    setStatus('saving')
    try {
      await save()
      setStatus('saved')
      setTimeout(() => setStatus((s) => (s === 'saved' ? 'idle' : s)), 2000)
    } catch (e) {
      console.error('autosave failed', e)
      setStatus('error')
    } finally {
      savingRef.current = false
    }
  }, [save])

  const markDirty = useCallback(() => {
    setStatus('dirty')
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(flush, delay)
  }, [flush, delay])

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  return { status, markDirty, flushNow: flush }
}
